import { useState } from "react";
import { Linkedin, Copy, Check, Share2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "./Reveal";
import { useLang } from "../../i18n/LanguageContext";

const btnCls =
  "chamfer-sm inline-flex items-center gap-2 px-4 py-2.5 font-mono-tech text-[10.5px] font-semibold uppercase tracking-[0.15em] transition-all duration-200 hover:-translate-y-0.5";

export const PilotShare = () => {
  const { lang, t } = useLang();
  const s = t.pilot.share;
  const [copied, setCopied] = useState(false);

  const shareUrl = `https://margtor.africa/share/pilot/${lang === "en" ? "" : `?lang=${lang}`}`;
  const post = `${s.post}\n\n${shareUrl}\n\n${s.hashtags}`;

  const copyPost = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(post);
      } else {
        const ta = document.createElement("textarea");
        ta.value = post;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        if (!ok) throw new Error("copy failed");
      }
      setCopied(true);
      toast.success(s.copiedTitle, { description: s.copiedBody });
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error(s.copyFailed);
    }
  };

  const nativeShare = () => navigator.share({ title: s.cardTitle, text: s.post, url: shareUrl }).catch(() => {});
  const canNativeShare = typeof navigator !== "undefined" && !!navigator.share;

  return (
    <Reveal delay={0.1}>
      <div
        data-testid="pilot-share"
        className="mt-8 flex flex-col md:flex-row md:items-center gap-6 border border-[#f8f7f4]/10 bg-[#f8f7f4]/[0.03] p-4 sm:p-5"
      >
        <a
          href={shareUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="pilot-share-preview"
          className="chamfer-sm relative block w-full md:w-72 shrink-0 overflow-hidden border border-[#f8f7f4]/15 transition-transform duration-300 hover:scale-[1.02]"
        >
          <img src="/og-pilot.jpg" alt={s.cardAlt} width={1200} height={630} loading="lazy" className="block w-full h-auto" />
          <span className="absolute right-2 top-2 bg-ink/80 px-2 py-0.5 font-mono-tech text-[9px] uppercase tracking-[0.2em] text-[#f8f7f4]/80">
            {s.previewTag}
          </span>
        </a>
        <div className="min-w-0 flex-1">
          <p className="font-mono-tech text-[0.65rem] tracking-[0.28em] uppercase text-[#e63946]" data-testid="pilot-share-eyebrow">
            {s.eyebrow}
          </p>
          <p className="mt-2 text-sm text-[#f8f7f4]/65 max-w-xl">{s.desc}</p>
          <div className="mt-4 flex flex-wrap gap-2.5">
            <a
              href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="pilot-share-linkedin"
              className={`${btnCls} bg-[#0a66c2] text-white hover:bg-[#0958a8]`}
            >
              <Linkedin className="h-4 w-4" />
              {s.linkedin}
            </a>
            <button
              type="button"
              onClick={copyPost}
              data-testid="pilot-share-copy"
              className={`${btnCls} border border-[#f8f7f4]/25 text-[#f8f7f4]/85 hover:border-[#e63946] hover:text-[#f8f7f4]`}
            >
              {copied ? <Check className="h-4 w-4 text-[#3ddc84]" /> : <Copy className="h-4 w-4" />}
              {copied ? s.copied : s.copy}
            </button>
            <a
              href={`https://wa.me/?text=${encodeURIComponent(post)}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="pilot-share-whatsapp"
              className={`${btnCls} border border-[#f8f7f4]/25 text-[#f8f7f4]/85 hover:border-[#25d366] hover:text-[#f8f7f4]`}
            >
              <MessageCircle className="h-4 w-4" />
              WhatsApp
            </a>
            {canNativeShare && (
              <button
                type="button"
                onClick={nativeShare}
                data-testid="pilot-share-native"
                className={`${btnCls} border border-[#f8f7f4]/25 text-[#f8f7f4]/85 hover:border-[#e63946] hover:text-[#f8f7f4]`}
              >
                <Share2 className="h-4 w-4" />
                {s.more}
              </button>
            )}
          </div>
        </div>
      </div>
    </Reveal>
  );
};
